import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import {
  BeakerIcon,
  BookmarkIcon,
  CogIcon,
  AdjustmentsHorizontalIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import PageHero from '../components/ui/PageHero';
import NutritionSectionNav from '../components/nutrition/NutritionSectionNav';
import NutritionQuickActionsMenu from '../components/nutrition/NutritionQuickActionsMenu';
import NutritionOverviewPanel from '../components/nutrition/NutritionOverviewPanel';
import NutritionDailyMixesPanel from '../components/nutrition/NutritionDailyMixesPanel';
import NutritionPlansPanel from '../components/nutrition/NutritionPlansPanel';
import NutritionReferencePanel from '../components/nutrition/NutritionReferencePanel';
import NutritionReportsPanel from '../components/nutrition/NutritionReportsPanel';
import FeedStock from './FeedStock';
import FeedStockAnalyticsPane from '../components/FeedStockAnalyticsPane';
import DailyFeedMixForm from '../components/DailyFeedMixForm';
import FeedMixOptimizer from '../components/FeedMixOptimizer';
import FeedMixTemplateManager from '../components/FeedMixTemplateManager';
import BatchOperationsModal from '../components/BatchOperationsModal';
import DailyFeedMixViewModal from '../components/DailyFeedMixViewModal';
import {
  parseNutritionSearchParams,
  buildNutritionSearchParams,
  NutritionSectionId,
  NutritionPlansView,
  NutritionReferenceView,
  NutritionReportsView,
} from '../components/nutrition/nutritionManagementNav';
import { nutritionApi, DailyFeedMix, NutritionalTrendData, NutritionalAnalytics, CreateDailyFeedMixDto } from '../api/nutrition';
import { useFeedStock } from '../hooks/useFeedStock';

const NutritionManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { section, view } = parseNutritionSearchParams(searchParams);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [trends, setTrends] = useState<{ data: NutritionalTrendData[]; analytics: NutritionalAnalytics } | null>(null);
  const [dailyMixes, setDailyMixes] = useState<DailyFeedMix[]>([]);

  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedLocation, setSelectedLocation] = useState('');

  const [showCreateMixModal, setShowCreateMixModal] = useState(false);
  const [showOptimizerModal, setShowOptimizerModal] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showBatchOperations, setShowBatchOperations] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMix, setSelectedMix] = useState<DailyFeedMix | null>(null);
  const [selectedMixes, setSelectedMixes] = useState<DailyFeedMix[]>([]);
  const [editingMix, setEditingMix] = useState<DailyFeedMix | null>(null);
  const [optimizerPrefill, setOptimizerPrefill] = useState<Partial<CreateDailyFeedMixDto> | null>(null);

  const { feedStocks, statistics } = useFeedStock();

  const navigateSection = useCallback(
    (nextSection: NutritionSectionId, nextView?: string) => {
      const params = new URLSearchParams();
      if (nextSection !== 'overview') params.set('section', nextSection);
      if (nextView) params.set('view', nextView);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const promises = [
        nutritionApi.getNutritionalDashboard(30, selectedLocation),
        nutritionApi.getNutritionalTrends({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          location: selectedLocation,
        }),
        nutritionApi.getDailyFeedMixes({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          location: selectedLocation,
        }),
      ];

      const results = await Promise.allSettled(promises);

      if (results[0].status === 'fulfilled') {
        setDashboardData(results[0].value);
      } else {
        setError(`Dashboard: ${(results[0].reason as Error)?.message || 'Unknown error'}`);
      }

      if (results[1].status === 'fulfilled') {
        setTrends(results[1].value as { data: NutritionalTrendData[]; analytics: NutritionalAnalytics });
      } else {
        setError(`Trends: ${(results[1].reason as Error)?.message || 'Unknown error'}`);
      }

      if (results[2].status === 'fulfilled') {
        setDailyMixes(results[2].value as DailyFeedMix[]);
      } else {
        setError(`Daily mixes: ${(results[2].reason as Error)?.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load nutritional data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [dateRange, selectedLocation]);

  useEffect(() => {
    if (section === 'plans' && view === 'templates') {
      setShowTemplateManager(true);
    }
  }, [section, view]);

  const navCounts = useMemo(
    () => ({
      inventory: statistics?.totalItems ?? feedStocks.length,
      mixes: dailyMixes.length,
      alerts: dashboardData?.alerts?.length ?? 0,
    }),
    [statistics, feedStocks.length, dailyMixes.length, dashboardData],
  );

  const handleCreateMix = async (mixData: any) => {
    try {
      if (editingMix?.id) {
        await nutritionApi.updateDailyFeedMix(editingMix.id, mixData);
      } else {
        await nutritionApi.createDailyFeedMix(mixData);
      }
      setShowCreateMixModal(false);
      setEditingMix(null);
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingMix ? 'update' : 'create'} feed mix`);
    }
  };

  const handleApproveMix = async (mixId: string, approved: boolean) => {
    try {
      await nutritionApi.approveDailyFeedMix(mixId, approved, 'Current User');
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message || 'Failed to update approval status');
    }
  };

  const handleViewMix = (mix: DailyFeedMix) => {
    setSelectedMix(mix);
    setShowViewModal(true);
  };

  const handleEditMix = (mix: DailyFeedMix) => {
    setEditingMix(mix);
    setShowCreateMixModal(true);
    setShowViewModal(false);
  };

  const handleDeleteMix = async (mixId: string) => {
    try {
      await nutritionApi.deleteDailyFeedMix(mixId);
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete feed mix');
      throw err;
    }
  };

  const handleDuplicateMix = (mix: DailyFeedMix) => {
    const duplicateData = {
      ...mix,
      feedDate: format(new Date(), 'yyyy-MM-dd'),
      notes: `Duplicate of ${mix.feedDate} mix`,
      metadata: undefined,
    };
    setEditingMix(duplicateData as DailyFeedMix);
    setShowCreateMixModal(true);
    setShowViewModal(false);
  };

  const renderContent = () => {
    switch (section) {
      case 'overview':
        return (
          <NutritionOverviewPanel
            dashboardData={dashboardData}
            trends={trends}
            dailyMixes={dailyMixes}
            selectedLocation={selectedLocation}
            onRefresh={loadDashboardData}
            onViewMixes={() => navigateSection('records', 'mixes')}
            onViewTrends={() => navigateSection('reports', 'nutritional')}
          />
        );
      case 'records':
        if (view === 'inventory') {
          return <FeedStock embedded />;
        }
        if (view === 'usage') {
          return <FeedStockAnalyticsPane />;
        }
        return (
          <NutritionDailyMixesPanel
            dailyMixes={dailyMixes}
            selectedMixes={selectedMixes}
            onSelectedMixesChange={setSelectedMixes}
            onView={handleViewMix}
            onEdit={handleEditMix}
            onDuplicate={handleDuplicateMix}
            onApprove={handleApproveMix}
            onDelete={handleDeleteMix}
          />
        );
      case 'plans':
        return (
          <NutritionPlansPanel
            view={view as NutritionPlansView}
            selectedLocation={selectedLocation}
            showTemplateManager={showTemplateManager}
            onCloseTemplateManager={() => {
              setShowTemplateManager(false);
              if (view === 'templates') navigateSection('plans', 'optimizer');
            }}
            onRefresh={loadDashboardData}
            onSelectTemplate={() => setShowTemplateManager(true)}
            onOpenInMixForm={(initialData) => {
              setOptimizerPrefill(initialData);
              setEditingMix(null);
              setShowCreateMixModal(true);
            }}
            onCreatedForDispense={() => {
              loadDashboardData();
              navigateSection('records', 'mixes');
            }}
          />
        );
      case 'reference':
        return <NutritionReferencePanel view={view as NutritionReferenceView} />;
      case 'reports':
        return (
          <NutritionReportsPanel
            view={view as NutritionReportsView}
            selectedLocation={selectedLocation}
            dateRange={dateRange}
            trends={trends}
            onReportGenerated={loadDashboardData}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading && !dashboardData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      {error && (
        <div className="rounded-lg border-l-4 border-red-400 bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
            <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <PageHero
        title="Nutrition Management"
        subtitle="Feed inventory, daily mixes, and flock nutritional planning in one place"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <NutritionQuickActionsMenu
              onNewFeedMix={() => {
                setEditingMix(null);
                setShowCreateMixModal(true);
              }}
              onOptimizeMix={() => setShowOptimizerModal(true)}
              onManageTemplates={() => {
                navigateSection('plans', 'templates');
                setShowTemplateManager(true);
              }}
              onBatchOperations={
                selectedMixes.length > 0 ? () => setShowBatchOperations(true) : undefined
              }
              batchCount={selectedMixes.length}
              onAddFeedStock={() => navigateSection('records', 'inventory')}
              onViewReports={() => navigateSection('reports', 'nutritional')}
            />
            <button
              type="button"
              onClick={() => {
                setEditingMix(null);
                setShowCreateMixModal(true);
              }}
              className="btn btn-primary text-sm focus-pop"
            >
              <BeakerIcon className="h-4 w-4" />
              New feed mix
            </button>
            {selectedMixes.length > 0 && section === 'records' && view === 'mixes' && (
              <button
                type="button"
                onClick={() => setShowBatchOperations(true)}
                className="btn btn-secondary text-sm focus-pop"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                Batch ({selectedMixes.length})
              </button>
            )}
            {section === 'plans' && view === 'templates' && (
              <button
                type="button"
                onClick={() => setShowTemplateManager(true)}
                className="btn btn-secondary text-sm focus-pop"
              >
                <BookmarkIcon className="h-4 w-4" />
                Templates
              </button>
            )}
            {section === 'plans' && view === 'optimizer' && (
              <button
                type="button"
                onClick={() => setShowOptimizerModal(true)}
                className="btn btn-secondary text-sm focus-pop"
              >
                <CogIcon className="h-4 w-4" />
                Optimize mix
              </button>
            )}
          </div>
        }
      />

      {dashboardData?.alerts?.length > 0 && section === 'overview' && (
        <div className="space-y-2">
          {dashboardData.alerts.slice(0, 3).map((alert: any, index: number) => (
            <div
              key={index}
              className={`rounded-lg border p-3 text-sm ${
                alert.severity === 'high'
                  ? 'border-red-200 bg-red-50 text-red-800'
                  : alert.severity === 'medium'
                    ? 'border-yellow-200 bg-yellow-50 text-yellow-800'
                    : 'border-cyan-200 bg-cyan-50 text-cyan-800'
              }`}
            >
              <strong className="uppercase">{alert.severity}</strong>: {alert.message}
            </div>
          ))}
          {dashboardData.alerts.length > 3 && (
            <p className="text-xs text-slate-500">+ {dashboardData.alerts.length - 3} more alerts in the sidebar panel</p>
          )}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Date range</label>
            <div className="mt-1 flex gap-2">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="block w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500"
              />
              <span className="self-center text-slate-500">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="block w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="mt-1 block w-48 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-cyan-500"
            >
              <option value="">All locations</option>
              <option value="Main Aviary">Main Aviary</option>
              <option value="Breeding Area">Breeding Area</option>
              <option value="Quarantine">Quarantine</option>
            </select>
          </div>
        </div>
      </div>

      <NutritionSectionNav section={section} view={view} counts={navCounts} onSectionChange={navigateSection} />

      <div key={`${section}-${view}`} className="glass-panel rounded-2xl p-4 focus:outline-none sm:p-6">
        {renderContent()}
      </div>

      {showCreateMixModal && (
        <DailyFeedMixForm
          key={editingMix?.id ?? optimizerPrefill?.feedDate ?? 'new-mix'}
          isOpen={showCreateMixModal}
          onClose={() => {
            setShowCreateMixModal(false);
            setEditingMix(null);
            setOptimizerPrefill(null);
          }}
          onSubmit={handleCreateMix}
          initialData={editingMix || optimizerPrefill || undefined}
          editMode={!!editingMix?.id}
        />
      )}

      {showViewModal && (
        <DailyFeedMixViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedMix(null);
          }}
          feedMix={selectedMix}
          onEdit={handleEditMix}
          onDelete={handleDeleteMix}
          onDuplicate={handleDuplicateMix}
          onApprove={handleApproveMix}
        />
      )}

      {showOptimizerModal && (
        <FeedMixOptimizer
          isModal
          isOpen={showOptimizerModal}
          onClose={() => setShowOptimizerModal(false)}
          onOptimize={loadDashboardData}
          selectedLocation={selectedLocation}
          onOpenInMixForm={(initialData) => {
            setOptimizerPrefill(initialData);
            setEditingMix(null);
            setShowOptimizerModal(false);
            setShowCreateMixModal(true);
          }}
          onCreatedForDispense={() => {
            loadDashboardData();
            setShowOptimizerModal(false);
            navigateSection('records', 'mixes');
          }}
        />
      )}

      {showTemplateManager && section !== 'plans' && (
        <FeedMixTemplateManager
          isOpen={showTemplateManager}
          onClose={() => setShowTemplateManager(false)}
          onSelectTemplate={() => setShowTemplateManager(false)}
          selectedComponents={[]}
        />
      )}

      {showBatchOperations && (
        <BatchOperationsModal
          isOpen={showBatchOperations}
          onClose={() => setShowBatchOperations(false)}
          selectedMixes={selectedMixes}
          onOperationComplete={() => {
            setSelectedMixes([]);
            loadDashboardData();
          }}
        />
      )}
    </div>
  );
};

export function FeedStockRedirect() {
  const target = buildNutritionSearchParams('records', 'inventory');
  return <Navigate to={`/nutrition-management${target}`} replace />;
}

export function NutritionalTrackingRedirect() {
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');
  const view = searchParams.get('view');
  if (section || view) {
    const params = new URLSearchParams(searchParams);
    return <Navigate to={`/nutrition-management?${params.toString()}`} replace />;
  }
  return <Navigate to="/nutrition-management" replace />;
}

export default NutritionManagement;
